import { Reveal } from "../Reveal";
import { ArrowDown } from "lucide-react";

const HERO_IMG =
  "https://static.prod-images.emergentagent.com/jobs/3adc9213-dab6-4851-9c35-a0c85e7e3dfd/images/6b02a66fef2aa11ab3ccd7b58b7ba4fba54e8c65cb203a884a5cc9ce43d8943f.png";

export const Hero = () => {
  const scrollNext = () => {
    document.querySelector("#capabilities")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="house"
      data-testid="hero-section"
      className="relative min-h-screen w-full overflow-hidden bg-[#F9F8F6]"
    >
      {/* Top spacing for fixed nav */}
      <div className="h-24 md:h-28" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12 pb-20 md:pb-28">
        {/* Editorial header rail */}
        <div className="flex items-center justify-between mb-10 md:mb-16">
          <span
            data-testid="hero-since-tag"
            className="text-[11px] tracking-[0.3em] uppercase text-[#0F1B3D]/60 font-medium"
          >
            01 — House of Shoozix
          </span>
          <span className="hidden md:inline text-[11px] tracking-[0.3em] uppercase text-[#0F1B3D]/60 font-medium">
            Agra · Noida · India
          </span>
        </div>

        {/* Headline + image grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-end">
          {/* Headline */}
          <div className="lg:col-span-7">
            <Reveal>
              <h1
                data-testid="hero-headline"
                className="font-serif text-[#0F1B3D] text-[15vw] leading-[0.92] md:text-[12vw] lg:text-[8.4vw] tracking-tight"
              >
                Leather, <span className="italic font-light">made</span>
                <br />
                by hand. <span className="text-[#B8945F]">Exported</span>
                <br />
                with <span className="italic font-light">trust.</span>
              </h1>
            </Reveal>

            <Reveal delay={250}>
              <div className="mt-10 md:mt-14 flex flex-col sm:flex-row sm:items-end gap-8 sm:gap-12">
                <div className="h-px w-24 bg-[#B8945F] origin-left animate-draw-line" />
                <p
                  data-testid="hero-subtext"
                  className="max-w-md text-[15px] md:text-base leading-relaxed text-[#1A1A1A]/75"
                >
                  Shoozix International LLP crafts <em className="font-serif text-[#0F1B3D]">footwear, garments &amp; accessories</em> for boutique brands and private labels across the world — from sample to shipment, in weeks, not months.
                </p>
              </div>
            </Reveal>

            <Reveal delay={500}>
              <div className="mt-10 flex flex-wrap items-center gap-4">
                <a
                  href="#capabilities"
                  onClick={(e) => {
                    e.preventDefault();
                    document.querySelector("#capabilities")?.scrollIntoView({ behavior: "smooth" });
                  }}
                  data-testid="hero-cta-explore"
                  className="group inline-flex items-center gap-3 bg-[#0F1B3D] text-[#F9F8F6] px-7 py-4 text-[12px] tracking-[0.22em] uppercase font-medium hover:bg-[#B8945F] transition-colors duration-500"
                >
                  <span>Explore the House</span>
                  <ArrowDown className="w-4 h-4 transition-transform duration-500 group-hover:translate-y-1" />
                </a>
                <a
                  href="#contact"
                  onClick={(e) => {
                    e.preventDefault();
                    document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" });
                  }}
                  data-testid="hero-cta-contact"
                  className="inline-flex items-center gap-3 border border-[#0F1B3D]/30 text-[#0F1B3D] px-7 py-4 text-[12px] tracking-[0.22em] uppercase font-medium hover:bg-[#0F1B3D] hover:text-[#F9F8F6] hover:border-[#0F1B3D] transition-all duration-500"
                >
                  Request a Sample
                </a>
              </div>
            </Reveal>
          </div>

          {/* Image column */}
          <div className="lg:col-span-5">
            <Reveal delay={300}>
              <div className="relative zoom-container">
                <div className="absolute -top-4 -left-4 w-24 h-24 border-t border-l border-[#B8945F]/60 z-10" />
                <div className="absolute -bottom-4 -right-4 w-24 h-24 border-b border-r border-[#B8945F]/60 z-10" />
                <img
                  src={HERO_IMG}
                  alt="Master leatherworker hands stitching premium leather"
                  data-testid="hero-image"
                  className="w-full h-[60vh] md:h-[68vh] object-cover animate-fade-in"
                />
                <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between text-[#F9F8F6] z-10">
                  <span className="text-[10px] tracking-[0.3em] uppercase font-medium">
                    Hand-stitch · Agra Atelier
                  </span>
                  <span className="text-[10px] tracking-[0.3em] uppercase font-medium">
                    Est. Excellence
                  </span>
                </div>
              </div>
            </Reveal>
          </div>
        </div>

        {/* Scroll cue */}
        <div className="mt-16 md:mt-24 flex items-center justify-between">
          <button
            type="button"
            onClick={scrollNext}
            data-testid="hero-scroll-cue"
            className="group flex items-center gap-3 text-[11px] tracking-[0.3em] uppercase text-[#0F1B3D]/70 hover:text-[#0F1B3D] transition"
          >
            <span className="w-10 h-px bg-current group-hover:w-16 transition-all duration-500" />
            <span>Scroll</span>
          </button>
          <div className="hidden md:flex items-center gap-8">
            <Stat number="20+" label="Countries Served" />
            <Stat number="100" label="Pair Min. MOQ" />
            <Stat number="14d" label="Sample Window" />
          </div>
        </div>
      </div>
    </section>
  );
};

const Stat = ({ number, label }) => (
  <div className="flex flex-col items-end">
    <span className="font-serif text-3xl text-[#0F1B3D] leading-none">{number}</span>
    <span className="text-[10px] tracking-[0.3em] uppercase text-[#0F1B3D]/55 mt-1">
      {label}
    </span>
  </div>
);
